import { FC, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Flex, Loader, Table, Text } from '@mantine/core';

import { useStyles } from './styles';
import { Th } from './th';
import { IColumnData, IRowData, ISimpleTableProps } from './types';
import { useTranslation } from 'react-i18next';
import { Tr } from './tr';
import { ExpandedTR } from '../expanded-row';
import { IconFaceIdError } from '@tabler/icons';

import { themeColors } from '@/theme/colors';

export const SimpleTable: FC<ISimpleTableProps> = ({
  dataCy = 'simpleTable',
  rowData,
  // setRowData,
  columns,
  minWidth,
  height,
  width,
  selection,
  handleRowClick,
  getNextData,
  allDataLength,
  error,
  scrollRef,
  isLoading,
  isFetching,
  sort,
  setSort,
  isExpanded
}) => {
  const { classes, cx } = useStyles();
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const setSorting = (field: keyof IRowData) => {
    if (setSort) {
      setSort({
        SortOrder: field === sort?.SortField ? !sort.SortOrder : false,
        SortField: field
      });
    }
  };

  const rows = rowData?.map((row: IRowData, index: number) => {
    return isExpanded ? (
      <ExpandedTR
        key={index}
        columns={columns}
        row={row}
        selection={selection}
        handleRowClick={handleRowClick}
      />
    ) : (
      <Tr
        key={index}
        columns={columns}
        row={row}
        selection={selection}
        handleRowClick={handleRowClick}
      />
    );
  });

  const columnsRender = columns.map((col: IColumnData) => (
    <Th
      key={col.dataIndex}
      width={col.width}
      sorted={sort?.SortField === col.dataIndex}
      reversed={!!sort?.SortOrder}
      onSort={() => col.hasSort && col.title && setSorting(col.dataIndex)}
      hideSort={!col.hasSort}
      hideColumn={col.hideColumn}
    >
      {col.title}
    </Th>
  ));

  const handleScroll = () => {
    setScrolled(true);
  };

  return (
    <Box sx={{ width }}>
      <InfiniteScroll
        ref={scrollRef}
        dataLength={rowData?.length}
        next={getNextData}
        hasMore={allDataLength > rowData.length}
        loader={
          <>
            {Boolean(rowData.length) && (
              <Box className={classes.infiniteScrollMoreData}>
                <Loader size="sm" />
              </Box>
            )}
          </>
        }
        className={classes.infiniteScroll}
        height={height}
        onScroll={handleScroll}
      >
        <Table horizontalSpacing="md" data-atribute={dataCy} verticalSpacing="xs" sx={{ minWidth }}>
          <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
            <tr>{columnsRender}</tr>
          </thead>
          <tbody>
            {(isLoading || isFetching) && (
              <tr className={classes.tr} style={{ height }}>
                <td colSpan={columns.length}>
                  <Box className={classes.infiniteScrollMoreData}>
                    <Loader size="sm" />
                  </Box>
                </td>
              </tr>
            )}

            {rowData?.length === 0 && (
              <tr className={classes.tr} style={{ height }}>
                <td colSpan={columns.length}>
                  {error ? (
                    <Flex justify="center" align="center" gap="5px">
                      <IconFaceIdError color={themeColors.brandGray[0]} stroke={1.8} />
                      <Text fw={500} ta="center" c={themeColors.brandGray[0]}>
                        {t('dataError')}
                      </Text>
                    </Flex>
                  ) : (
                    <Text fw={500} ta="center" c={themeColors.brandGray[0]}>
                      {t('noData')}
                    </Text>
                  )}
                </td>
              </tr>
            )}
            {rowData?.length > 0 && !isLoading && rows}
          </tbody>
        </Table>
      </InfiniteScroll>
    </Box>
  );
};
